import React, { useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

const RemediationStepsPanel = ({
  remediationData,
  isAwsCheck,
  isAzureCheck,
  isGcpCheck,
}) => {
  const theme = useTheme();
  const [uiStepsExpanded, setUiStepsExpanded] = useState(false);
  const [programmaticExpanded, setProgrammaticExpanded] = useState(false);

  const hasRemediationSteps =
    remediationData?.uiSteps?.length > 0 || remediationData?.programmaticSteps;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadChecksTemplate = (path = "") => {
    if (!path || !process.env.REACT_APP_POLICY_DOC_URL) return;
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_POLICY_DOC_URL}${path}`;
    link.download = path.split("/").pop() || "template";
    link.click();
  };

  const openRemediationLink = (rawUrl = "") => {
    if (!rawUrl) return;
    const trimmed = rawUrl.trim();
    if (/^\/?checks_templates\//i.test(trimmed)) {
      downloadChecksTemplate(trimmed);
      return;
    }
    const href = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `${window.location.origin}/${trimmed.replace(/^\/+/, "")}`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const renderBoldText = (text) => {
    if (!text) return text;
    const parts = [];
    const regex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(
        <Box key={`bold-${keyIndex++}`} component="span" sx={{ fontWeight: 600 }}>
          {match[1]}
        </Box>
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const renderTextWithLinks = (text) => {
    if (!text) return text;

    const combinedRegex =
      /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = combinedRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      if (match[1]) {
        const linkText = match[2];
        const linkUrl = (match[3] || "").trim();
        parts.push(
          <Typography
            key={`link-${keyIndex++}`}
            component="span"
            onClick={() => openRemediationLink(linkUrl)}
            sx={{
              fontSize: "inherit",
              color: theme.palette.primary.main,
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
                color: theme.palette.primary.dark,
              },
            }}
          >
            {linkText}
          </Typography>
        );
      } else if (match[4]) {
        const boldText = match[5];
        parts.push(
          <Typography
            key={`bold-${keyIndex++}`}
            component="span"
            sx={{ fontSize: "inherit", fontWeight: 600 }}
          >
            {boldText}
          </Typography>
        );
      } else if (match[6]) {
        const italicText = match[7];
        parts.push(
          <Typography
            key={`italic-${keyIndex++}`}
            component="span"
            sx={{ fontSize: "inherit", fontWeight: 600 }}
          >
            {italicText}
          </Typography>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  if (!hasRemediationSteps) return null;

  return (
    <Box
      sx={{
        pt: 3,
        mt: 3,
        borderTop: `1px solid ${theme.customColors.background.tableRow}`,
        px: 0,
        pb: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      {remediationData.uiSteps.length > 0 && (
        <Alert
          severity="info"
          icon={false}
          sx={{
            borderRadius: "12px",
            bgcolor: theme.customColors.background.info,
            border: `1px solid ${theme.customColors.status.info.main}20`,
            boxShadow: theme.customColors.shadow.main,
            "& .MuiAlert-message": {
              color: theme.customColors.text.primary,
              width: "100%",
              padding: 0,
            },
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 0.5,
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setUiStepsExpanded(!uiStepsExpanded)}
            >
              <InfoIcon
                sx={{
                  fontSize: 20,
                  color: theme.palette.info.main,
                  mt: 0.1,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    letterSpacing: "-0.01em",
                    mb: uiStepsExpanded ? 2 : 0,
                  }}
                >
                  {isAwsCheck || isAzureCheck || isGcpCheck
                    ? "UI Remediation Steps"
                    : "Remediation Steps"}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0, mt: 0.1 }}>
                {uiStepsExpanded ? (
                  <ExpandLessIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.secondary,
                      transition: "transform 0.2s ease",
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.secondary,
                      transition: "transform 0.2s ease",
                    }}
                  />
                )}
              </Box>
            </Box>
            <Collapse in={uiStepsExpanded}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  pl: 5.5,
                  pb: 1,
                }}
              >
                {remediationData.uiSteps.map((step, index) => {
                  const stepText =
                    typeof step === "string" ? step : step.description || "";
                  return (
                    <Box
                      key={index}
                      sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                    >
                      <Box
                        sx={{
                          minWidth: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: `${theme.palette.info.main}15`,
                          border: `1px solid ${theme.palette.info.main}30`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: theme.palette.info.main,
                            lineHeight: 1,
                          }}
                        >
                          {step.order || index + 1}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{
                          fontSize: "0.875rem",
                          color: theme.palette.text.primary,
                          lineHeight: 1.7,
                          flex: 1,
                          fontWeight: 400,
                          pt: 0.25,
                        }}
                      >
                        {renderTextWithLinks(stepText)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        </Alert>
      )}

      {remediationData.programmaticSteps && (
        <Alert
          severity="info"
          icon={false}
          sx={{
            borderRadius: "12px",
            bgcolor: theme.customColors.background.info,
            border: `1px solid ${theme.customColors.status.info.main}20`,
            boxShadow: theme.customColors.shadow.main,
            "& .MuiAlert-message": {
              color: theme.customColors.text.primary,
              width: "100%",
              padding: 0,
            },
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setProgrammaticExpanded(!programmaticExpanded)}
            >
              <InfoIcon
                sx={{
                  fontSize: 20,
                  color: theme.palette.info.main,
                  mt: 0.1,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    letterSpacing: "-0.01em",
                    mb: programmaticExpanded ? 2 : 0,
                  }}
                >
                  Programmatic Remediation Steps
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0, mt: 0.1 }}>
                {programmaticExpanded ? (
                  <ExpandLessIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.secondary,
                      transition: "transform 0.2s ease",
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.secondary,
                      transition: "transform 0.2s ease",
                    }}
                  />
                )}
              </Box>
            </Box>
            <Collapse in={programmaticExpanded}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pl: 5.5,
                }}
              >
                {remediationData.programmaticSteps.config?.variables?.length > 0 && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1.5,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Variables
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {remediationData.programmaticSteps.config.variables.map(
                        (variable, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.palette.background.paper,
                              borderRadius: "8px",
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                mb: 0.5,
                              }}
                            >
                              {variable.key}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8125rem",
                                color: theme.palette.text.secondary,
                                lineHeight: 1.5,
                              }}
                            >
                              {variable.description}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                )}

                {remediationData.programmaticSteps.config?.command && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1.5,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Command
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="code"
                        sx={{
                          fontSize: "0.8125rem",
                          fontFamily: "monospace",
                          color: theme.palette.text.primary,
                          flex: 1,
                          wordBreak: "break-all",
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {remediationData.programmaticSteps.config.command}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            remediationData.programmaticSteps.config.command
                          );
                        }}
                        sx={{
                          color: theme.palette.text.secondary,
                          flexShrink: 0,
                          mt: -0.5,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {remediationData.programmaticSteps.config?.command && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1.5,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Dry Run Command
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="code"
                        sx={{
                          fontSize: "0.8125rem",
                          fontFamily: "monospace",
                          color: theme.palette.text.primary,
                          flex: 1,
                          wordBreak: "break-all",
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {remediationData.programmaticSteps.config.command} --dry-run
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            `${remediationData.programmaticSteps.config.command} --dry-run`
                          );
                        }}
                        sx={{
                          color: theme.palette.text.secondary,
                          flexShrink: 0,
                          mt: -0.5,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {remediationData.programmaticSteps.config?.explanation?.length > 0 && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 2,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Explanation
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {remediationData.programmaticSteps.config.explanation.map(
                        (step, index) => {
                          const stepText = step.description || "";
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "flex-start",
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  backgroundColor: `${theme.palette.info.main}15`,
                                  border: `1px solid ${theme.palette.info.main}30`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  mt: 0.25,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: theme.palette.info.main,
                                    lineHeight: 1,
                                  }}
                                >
                                  {step.order || index + 1}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                  fontSize: "0.875rem",
                                  color: theme.palette.text.primary,
                                  lineHeight: 1.7,
                                  flex: 1,
                                  fontWeight: 400,
                                }}
                              >
                                {renderBoldText(stepText)}
                              </Typography>
                            </Box>
                          );
                        }
                      )}
                    </Box>
                  </Box>
                )}

                {remediationData.programmaticSteps.config?.example_usage && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1.5,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Example Usage
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="code"
                        sx={{
                          fontSize: "0.8125rem",
                          fontFamily: "monospace",
                          color: theme.palette.text.primary,
                          flex: 1,
                          wordBreak: "break-all",
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {remediationData.programmaticSteps.config.example_usage}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            remediationData.programmaticSteps.config.example_usage
                          );
                        }}
                        sx={{
                          color: theme.palette.text.secondary,
                          flexShrink: 0,
                          mt: -0.5,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default RemediationStepsPanel;
